/**
 * Form component
 */

import React, {
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from "react";
import {
    Form as FormikForm,
    Formik,
    FormikBag,
    FormikProps,
    useFormikContext,
} from "formik";
import type { VisitOptions } from "@inertiajs/core";
import { router, usePage } from "@inertiajs/react";
import Button from "../Button.js";
import Alert, { AlertList } from "../Alert.js";
import { merge } from "lodash";
import { AnyObjectSchema, ObjectSchema } from "yup";
import Modal, { ModalVariantProps } from "@/Components/Modal";
import { InvalidFieldClassName } from "@/Components/Form/FormConstants.ts";

export type LaravelValidationErrors = {
    [key: string]: string[];
};

interface FormSpecialContextInterface {
    submitClass?: string;
    submitTitle?: string;
    hideClear?: boolean;
    alwaysClearable?: boolean;
    formName?: string;
    clearTitle?: string;
    removeDefaultInputMargin?: boolean;
    alwaysDirty?: boolean;
    disabled?: boolean;
    readOnly?: boolean;
    hasErrors?: boolean;
    onClear?: () => void;
    status?: {
        [key: string]: never;
    };
    setStatus?: (state) => void;
}

export const FormSpecialContext =
    React.createContext<FormSpecialContextInterface | null>({});

type SubmissionButtonsProps = {
    onClear?: () => void;
};

export const SubmissionButtons: React.FC<SubmissionButtonsProps> = (props) => {
    const { isSubmitting, dirty, errors, handleReset, touched } =
        useFormikContext();

    const formSpecialContext = useContext(FormSpecialContext);

    const clearForm = () => {
        if (props.onClear) {
            props.onClear();
        }
        if (formSpecialContext.onClear) {
            formSpecialContext.onClear();
        }
        handleReset();
    };

    const calculateNumberOfErrors = (errors, touched) => {
        if (errors === undefined) return 0;
        return Object.keys(errors).reduce((total, current) => {
            if (Array.isArray(errors[current])) {
                for (let i = 0; i < errors[current].length; i++) {
                    total += calculateNumberOfErrors(
                        errors[current][i],
                        touched?.[current]?.[i] || {},
                    );
                }
            } else if (errors[current].length > 0 && touched?.[current]) {
                total += 1;
            }
            return total;
        }, 0);
    };

    const numErrors = calculateNumberOfErrors(errors, touched);

    return (
        <div className="flex items-center justify-between gap-4">
            <div>
                {numErrors > 0 && (
                    <div className="text-sm text-red-500">
                        {/* May reinstate icon later */}
                        {/*<ExclamationCircleIcon*/}
                        {/*    className="h-5 w-5 text-red-500 inline"*/}
                        {/*    aria-hidden="true"*/}
                        {/*/>{" "}*/}
                        <>
                            {numErrors === 1 && (
                                <>
                                    <span className="sr-only">There is </span>
                                    <strong>1</strong> error to correct
                                </>
                            )}
                            {numErrors !== 1 && (
                                <>
                                    <span className="sr-only">There are </span>
                                    <strong>{numErrors}</strong> errors to
                                    correct
                                </>
                            )}
                        </>
                    </div>
                )}
            </div>
            <div className="flex flex-row-reverse gap-4">
                <SubmitButton className="inline-flex justify-center" />

                {!formSpecialContext.hideClear && (
                    <>
                        <Button
                            type="button"
                            onClick={clearForm}
                            disabled={
                                isSubmitting ||
                                (!dirty && !formSpecialContext.alwaysClearable)
                            }
                            variant="secondary"
                            className="inline-flex justify-center"
                        >
                            {formSpecialContext.clearTitle || "Clear"}
                        </Button>{" "}
                    </>
                )}
            </div>
        </div>
    );
};

export const SubmitButton = ({ className }: { className?: string }) => {
    const { isSubmitting } = useFormikContext();

    const formSpecialContext = useContext(FormSpecialContext);

    return (
        <Button
            className={`${className} ${formSpecialContext.submitClass}`}
            type="submit"
            disabled={isSubmitting}
        >
            {formSpecialContext.submitTitle || "Submit"}
        </Button>
    );
};

export const UnknownError = () => {
    const { hasErrors } = useContext(FormSpecialContext);

    if (hasErrors) {
        return (
            <Alert
                className="mb-3"
                variant="error"
                title="An unknown error occurred"
                // handleDismiss={handleNetErrorDismiss}
            >
                Please check your form data and try again.
            </Alert>
        );
    }
    return null;
};

const HandleServerErrors = () => {
    const pageProps = usePage().props;

    const { formName, setStatus } = useContext(FormSpecialContext);

    // If we're scoped, get those errors, otherwise just the errors
    const errors = formName ? pageProps?.errors[formName] : pageProps.errors;

    const { setSubmitting } = useFormikContext();

    useEffect(() => {
        if (errors) {
            setStatus(errors);
            setSubmitting(false);
        }
    }, [setStatus, setSubmitting, errors]);

    return null;
};

export const SetServerErrors = ({
    errors,
}: {
    errors: LaravelValidationErrors;
}) => {
    const { setStatus } = useContext(FormSpecialContext);

    const { setSubmitting } = useFormikContext();

    useEffect(() => {
        if (errors) {
            setStatus(errors);
            setSubmitting(false);
        }
    }, [setStatus, setSubmitting, errors]);

    return null;
};

export const RenderServerErrors = () => {
    const context = useContext(FormSpecialContext);
    const errors = context.status;

    if (errors) {
        const errorList = [];
        Object.entries(errors).forEach(([key, value]) => {
            if (typeof value === "string") {
                errorList.push(<li key={key}>{value}</li>);
            } else if (Array.isArray(value)) {
                // @ts-expect-error Does not think it is an array
                value.forEach((v, idx) => {
                    errorList.push(<li key={`${key}_${idx}`}>{value}</li>);
                });
            }
        });

        if (errorList.length > 0) {
            return (
                <Alert
                    className="mb-3"
                    variant="error"
                    title="There are errors to correct"
                >
                    <AlertList>{errorList}</AlertList>
                </Alert>
            );
        }
    }

    return null;
};

export type FormRequestMethods =
    | "get"
    | "put"
    | "post"
    | "delete"
    | "head"
    | "connect"
    | "options"
    | "trace"
    | "patch";

export type FormProps<Values> = {
    initialValues?: Values;
    validationSchema:
        | ObjectSchema<Values>
        | (() => ObjectSchema<Values>)
        | AnyObjectSchema
        | (() => AnyObjectSchema);
    onSubmit?: (values: Values, formikBag: FormikBag<never, never>) => void;
    submitTitle?: string;
    submitClass?: string;
    hideClear?: boolean;
    hideErrors?: boolean;
    clearTitle?: string;
    onClear?: () => void;
    alwaysClearable?: boolean;
    hideDefaultButtons?: boolean;
    removeDefaultInputMargin?: boolean;
    action?: string;
    method?: FormRequestMethods;
    formName?: string;
    inertiaOptions?: VisitOptions;
    alwaysDirty?: boolean;
    children?: ReactNode;
    disabled?: boolean;
    readOnly?: boolean;
    onSuccess?: () => void;
    confirm?: {
        type?: ModalVariantProps;
        message: string | ReactNode;
        confirmText?: string;
    };
    /** Default is `false`. Control whether Formik should reset the form if `initialValues` changes (using deep equality). */
    enableReinitialize?: boolean;
    setFormApi?: (api: FormApi<Values>) => void;
};

export type FormApi<Values> = {
    resetForm: FormikProps<Values>["resetForm"];
    setErrors: FormikProps<Values>["setErrors"];
    setFieldError: FormikProps<Values>["setFieldError"];
    setFieldTouched: FormikProps<Values>["setFieldTouched"];
    submitForm: FormikProps<Values>["submitForm"];
    setFieldValue: FormikProps<Values>["setFieldValue"];
    setStatus: FormikProps<Values>["setStatus"];
    setSubmitting: FormikProps<Values>["setSubmitting"];
    setTouched: FormikProps<Values>["setTouched"];
    setValues: FormikProps<Values>["setValues"];
    validateForm: FormikProps<Values>["validateForm"];
    validateField: FormikProps<Values>["validateField"];
    registerField: FormikProps<Values>["registerField"];
    unregisterField: FormikProps<Values>["unregisterField"];
    setFormikState: FormikProps<Values>["setFormikState"];
};

const Form = <Values = any,>(props: FormProps<Values>) => {
    const {
        initialValues,
        validationSchema,
        onSubmit,
        submitTitle,
        submitClass,
        hideClear,
        hideErrors,
        clearTitle,
        onClear: onClearProp,
        alwaysClearable = false,
        hideDefaultButtons = false,
        removeDefaultInputMargin,
        action,
        method = "post",
        formName = null,
        inertiaOptions = {},
        alwaysDirty = false,
        disabled = false,
        readOnly = false,
        onSuccess,
        enableReinitialize = true,
        ...otherProps
    } = props;

    const formDomRef = useRef<HTMLDivElement>(null);

    const onClear = () => {
        if (onClearProp) {
            onClearProp();
        }

        const firstInput = formDomRef.current.querySelector("input");

        if (firstInput) {
            firstInput.focus();
        }
    };

    const [hasErrors, setHasErrors] = useState<boolean>(false);
    // const handleNetErrorDismiss = () => {
    //     setHasErrors(false);
    // };
    const [showConfirm, setShowConfirm] = useState<boolean>(false);
    const [confirmed, setConfirmed] = useState<boolean>(false);
    const [reinit, setReinit] = useState<boolean>(false);
    const [shouldSubmit, setShouldSubmit] = useState<boolean>(false);

    useEffect(() => {
        // If we get an invalid response, don't show any of the response
        // Instead show a warning to the user in the form
        return router.on("invalid", (event) => {
            setHasErrors(true);
            // When in production, show the dev error messages
            if (import.meta.env.PROD) {
                event.preventDefault();
            }
        });
    }, []);

    const onSubmitHandler = async (values: Values, formikBag) => {
        setStatus({});
        if (onSubmit) {
            // Escape hatch override
            try {
                await onSubmit(values, formikBag);
            } catch {
                setHasErrors(true);
            }
        } else {
            // Use default behaviour
            if (formName) {
                inertiaOptions.errorBag = formName;
            }

            if (props.confirm && !confirmed) {
                setShowConfirm(true);
            } else {
                setConfirmed(false);
                router[method](action, values, {
                    onSuccess: () => {
                        if (onSuccess) {
                            onSuccess();
                        }
                        formikBag.resetForm();
                        setReinit(true);
                        // if (page?.props?.form_initial_values) {
                        //     formikBag.setValues(page.props.form_initial_values);
                        // }
                    },
                    ...inertiaOptions,
                    // onError: (error) => {
                    //     console.log(error);
                    // },
                    onFinish: () => {
                        // Always mark as not submitting once done
                        formikBag.setSubmitting(false);
                    },
                });
            }
        }
    };

    type InitialValues = Values | null;
    const pageProps: Record<string, unknown> = usePage().props;

    const newInitialValues: InitialValues = formName
        ? // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          pageProps[formName]?.form_initial_values
        : pageProps.form_initial_values;
    const mergedValues = merge(initialValues, newInitialValues);

    const defaultConfirmVariant: ModalVariantProps = "danger";

    const [status, setStatusState] = useState(null);
    const setStatus = useCallback((state) => {
        setStatusState(state);
    }, []);

    useEffect(() => {
        if (reinit) {
            setReinit(false);
        }
    }, [reinit]);

    return (
        <FormSpecialContext.Provider
            value={{
                hideClear: hideClear,
                clearTitle: clearTitle,
                submitTitle: submitTitle,
                submitClass: submitClass,
                removeDefaultInputMargin: removeDefaultInputMargin,
                formName: formName,
                alwaysDirty: alwaysDirty,
                alwaysClearable: alwaysClearable,
                disabled: disabled,
                readOnly: readOnly,
                hasErrors: hasErrors,
                onClear: onClear,
                status: status,
                setStatus: setStatus,
            }}
        >
            <Formik<Values>
                initialValues={mergedValues}
                validationSchema={validationSchema}
                onSubmit={onSubmitHandler}
                enableReinitialize={enableReinitialize || reinit}
            >
                {(formikProps) => {
                    if (props.setFormApi) {
                        const formApi: FormApi<Values> = {
                            registerField: formikProps.registerField,
                            unregisterField: formikProps.unregisterField,
                            setSubmitting: formikProps.setSubmitting,
                            setStatus: formikProps.setStatus,
                            setFormikState: formikProps.setFormikState,
                            setFieldTouched: formikProps.setFieldTouched,
                            setFieldValue: formikProps.setFieldValue,
                            setFieldError: formikProps.setFieldError,
                            resetForm: formikProps.resetForm,
                            setErrors: formikProps.setErrors,
                            submitForm: formikProps.submitForm,
                            setTouched: formikProps.setTouched,
                            setValues: formikProps.setValues,
                            validateForm: formikProps.validateForm,
                            validateField: formikProps.validateField,
                        };

                        props.setFormApi(formApi);
                    }

                    const onConfirm = async () => {
                        setConfirmed(true);
                        setShowConfirm(false);
                        try {
                            await formikProps.submitForm();
                        } catch {
                            // Ignore
                        }
                    };

                    const onConfirmReject = () => {
                        setConfirmed(false);
                        setShowConfirm(false);
                    };

                    const submitCount = formikProps.submitCount;
                    const errors = formikProps.errors;
                    const isSubmitting = formikProps.isSubmitting;

                    const [lastSubmitCount, setLastSubmitCount] =
                        useState<number>(0);

                    useEffect(() => {
                        if (isSubmitting) {
                            return;
                        }

                        if (submitCount > lastSubmitCount) {
                            setLastSubmitCount(submitCount);
                        }

                        if (submitCount > lastSubmitCount && errors) {
                            // Focus first error field
                            const errorInputs =
                                formDomRef.current.getElementsByClassName(
                                    InvalidFieldClassName,
                                );

                            if (errorInputs.length > 0) {
                                const element = errorInputs[0];

                                let input:
                                    | HTMLInputElement
                                    | HTMLButtonElement
                                    | HTMLTextAreaElement =
                                    element.querySelector("input");

                                if (!input) {
                                    input = element.querySelector("button");
                                }

                                if (!input) {
                                    input = element.querySelector("textarea");
                                }

                                if (input) {
                                    input.focus();
                                }
                            }
                        }
                    }, [lastSubmitCount, submitCount, errors, isSubmitting]);

                    return (
                        <div ref={formDomRef}>
                            <FormikForm
                                {...otherProps}
                                onSubmit={(ev) => {
                                    ev.preventDefault();
                                    ev.stopPropagation();
                                    setShouldSubmit(true);
                                }}
                            >
                                {props.confirm && (
                                    <Modal
                                        variant={
                                            props.confirm.type ||
                                            defaultConfirmVariant
                                        }
                                        show={showConfirm}
                                        onClose={onConfirmReject}
                                        title="Are you sure?"
                                        buttons={
                                            <>
                                                <Button
                                                    id="confirm-yes"
                                                    variant={
                                                        props.confirm.type ||
                                                        defaultConfirmVariant
                                                    }
                                                    onClick={onConfirm}
                                                >
                                                    {props.confirm
                                                        .confirmText ||
                                                        "Confirm"}
                                                </Button>
                                                <Button
                                                    id="confirm-no"
                                                    variant="secondary"
                                                    onClick={onConfirmReject}
                                                >
                                                    Cancel
                                                </Button>
                                            </>
                                        }
                                    >
                                        {props.confirm.message}
                                    </Modal>
                                )}

                                <HandleServerErrors />

                                {!hideErrors && <RenderServerErrors />}

                                {!hideErrors && <UnknownError />}

                                {props.children}

                                {!hideDefaultButtons && (
                                    <div className="mt-5 sm:mt-4">
                                        <SubmissionButtons onClear={onClear} />
                                    </div>
                                )}

                                <SubmitHandler
                                    setShouldSubmit={setShouldSubmit}
                                    shouldSubmit={shouldSubmit}
                                />
                            </FormikForm>
                        </div>
                    );
                }}
            </Formik>
        </FormSpecialContext.Provider>
    );
};

type SubmitHandlerType = {
    shouldSubmit: boolean;
    setShouldSubmit: (value: boolean) => void;
};

const SubmitHandler = (props: SubmitHandlerType) => {
    const submitForm = useFormikContext().submitForm;

    useEffect(() => {
        if (props.shouldSubmit) {
            // Trigger submit
            props.setShouldSubmit(false);
            submitForm();
        }
    }, [props.shouldSubmit]);

    return null;
};

export default Form;
